import React, { useRef, useState, useEffect, useCallback } from 'react';
import MindMapNode from './MindMapNode';
import MindMapEdge from './MindMapEdge';
import MindMapDrawing from './MindMapDrawing';
import MindMapToolbar from './MindMapToolbar';
import MindMapMinimap from './MindMapMinimap';
import MindMapContextMenu from './MindMapContextMenu';
import useMindMap, { TOOL_MODES } from './useMindMap';

// Dot grid pattern component - fixed size dots
const DotGrid = () => {
  const spacing = 30;
  const dotSize = 1;
  
  return (
    <pattern
      id="dotGrid"
      width={spacing}
      height={spacing}
      patternUnits="userSpaceOnUse"
    >
      <circle
        cx={spacing / 2}
        cy={spacing / 2}
        r={dotSize}
        fill="rgba(255, 255, 255, 0.08)"
      />
    </pattern>
  );
};

const MindMapCanvas = ({ mapId = 'default' }) => {
  const containerRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [contextMenu, setContextMenu] = useState({ isOpen: false, x: 0, y: 0 });

  const {
    nodes,
    edges,
    data,
    selectedNodeId,
    editingNodeId,
    transform,
    isPanning,
    isDraggingNode,
    toolMode,
    setToolMode,
    saveStatus,
    isLoading,
    autoSaveEnabled,
    toggleAutoSave,
    hasUnsavedChanges,
    drawings,
    currentDrawing,
    drawColor,
    setDrawColor,
    startDrawing,
    continueDrawing,
    endDrawing,
    connectorStart,
    startConnector,
    endConnector,
    cancelConnector,
    addChildNode,
    addRootNode,
    updateNode,
    deleteNode,
    toggleCollapse,
    getChildren,
    isNodeVisible,
    setSelectedNodeId,
    setEditingNodeId,
    handlePanStart,
    handlePanMove,
    handlePanEnd,
    handleZoom,
    handleNodeDragStart,
    handleNodeDragMove,
    handleNodeDragEnd,
    resetView,
    zoomIn,
    zoomOut,
    setTransform,
    setSnapToGrid: setSnapToGridHook,
  } = useMindMap(mapId);

  // Update canvas size on resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setCanvasSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Prevent browser's native pinch-to-zoom on the entire document when mouse is over canvas
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isMouseOverCanvas = false;

    const handleMouseEnter = () => {
      isMouseOverCanvas = true;
    };

    const handleMouseLeave = () => {
      isMouseOverCanvas = false;
    };

    // Prevent native zoom at document level when mouse is over canvas
    const preventNativeZoom = (e) => {
      if (isMouseOverCanvas && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
      }
    };

    // Handle gesture events (Safari) at document level
    const preventGesture = (e) => {
      if (isMouseOverCanvas) {
        e.preventDefault();
      }
    };

    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);
    
    // Add to document to catch events before browser processes them
    document.addEventListener('wheel', preventNativeZoom, { passive: false });
    document.addEventListener('gesturestart', preventGesture, { passive: false });
    document.addEventListener('gesturechange', preventGesture, { passive: false });
    document.addEventListener('gestureend', preventGesture, { passive: false });

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('wheel', preventNativeZoom);
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      document.removeEventListener('gestureend', preventGesture);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when editing
      if (editingNodeId) return;

      if (e.key === 'Tab' && selectedNodeId) {
        e.preventDefault();
        addChildNode(selectedNodeId);
      }
      
      if (e.key === 'Enter' && selectedNodeId) {
        e.preventDefault();
        setEditingNodeId(selectedNodeId);
      }
      
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNodeId) {
        e.preventDefault();
        deleteNode(selectedNodeId);
      }
      
      if (e.key === 'Escape') {
        setSelectedNodeId(null);
        setContextMenu({ isOpen: false, x: 0, y: 0 });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeId, editingNodeId, addChildNode, deleteNode, setEditingNodeId, setSelectedNodeId]);

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu({ isOpen: false, x: 0, y: 0 });
    };
    
    if (contextMenu.isOpen) {
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu.isOpen]);

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback((screenX, screenY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (screenX - rect.left - transform.x) / transform.scale,
      y: (screenY - rect.top - transform.y) / transform.scale,
    };
  }, [transform]);

  // Handle mouse events for panning, dragging, drawing
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;

    const canvasPos = screenToCanvas(e.clientX, e.clientY);

    // Handle drawing tool
    if (toolMode === TOOL_MODES.DRAW) {
      startDrawing(canvasPos.x, canvasPos.y);
      return;
    }

    // Default: pan
    handlePanStart(e);
  }, [toolMode, screenToCanvas, startDrawing, handlePanStart]);

  const handleMouseMove = useCallback((e) => {
    // Handle drawing
    if (toolMode === TOOL_MODES.DRAW && currentDrawing) {
      const canvasPos = screenToCanvas(e.clientX, e.clientY);
      continueDrawing(canvasPos.x, canvasPos.y);
      return;
    }

    // Handle node dragging
    if (isDraggingNode) {
      handleNodeDragMove(e);
      return;
    }
    
    // Handle panning
    if (isPanning) {
      handlePanMove(e);
    }
  }, [toolMode, currentDrawing, screenToCanvas, continueDrawing, isDraggingNode, handleNodeDragMove, isPanning, handlePanMove]);

  const handleMouseUp = useCallback(() => {
    // End drawing
    if (toolMode === TOOL_MODES.DRAW && currentDrawing) {
      endDrawing();
    }

    handlePanEnd();
    handleNodeDragEnd();
  }, [toolMode, currentDrawing, endDrawing, handlePanEnd, handleNodeDragEnd]);

  // Handle node click for connector tool
  const handleNodeClickForConnector = useCallback((nodeId) => {
    if (toolMode !== TOOL_MODES.CONNECTOR) return false;
    
    if (!connectorStart) {
      startConnector(nodeId);
    } else {
      endConnector(nodeId);
      setToolMode(TOOL_MODES.SELECT);
    }
    return true;
  }, [toolMode, connectorStart, startConnector, endConnector, setToolMode]);

  // Context menu handler
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    setContextMenu({
      isOpen: true,
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  // Handle canvas click (deselect)
  const handleCanvasClick = useCallback((e) => {
    if (e.target === e.currentTarget || e.target.tagName === 'svg' || e.target.tagName === 'rect') {
      setSelectedNodeId(null);
      if (connectorStart) {
        cancelConnector();
      }
    }
  }, [setSelectedNodeId, connectorStart, cancelConnector]);

  // Get cursor based on tool mode
  const getCursor = () => {
    if (isPanning) return 'grabbing';
    switch (toolMode) {
      case TOOL_MODES.DRAW: return 'crosshair';
      case TOOL_MODES.CONNECTOR: return connectorStart ? 'pointer' : 'crosshair';
      default: return 'grab';
    }
  };

  // Filter visible nodes (not hidden by collapsed ancestors)
  const visibleNodes = nodes.filter(node => isNodeVisible(node.id));
  const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
  
  // Filter edges to only show connections between visible nodes
  const visibleEdges = edges.filter(
    edge => visibleNodeIds.has(edge.sourceId) && visibleNodeIds.has(edge.targetId)
  );

  const selectedNode = selectedNodeId ? data.nodes[selectedNodeId] : null;

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full bg-[#0a0a0a] overflow-hidden select-none"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleZoom}
      onContextMenu={handleContextMenu}
      onClick={handleCanvasClick}
      style={{ 
        cursor: getCursor(),
        touchAction: 'none', // Prevent browser handling of touch/pinch gestures
      }}
    >
      <svg
        width={canvasSize.width}
        height={canvasSize.height}
        className="absolute inset-0"
      >
        <defs>
          <DotGrid />
        </defs>

        {/* Background with fixed dot grid - doesn't move with pan/zoom */}
        <rect
          width="100%"
          height="100%"
          fill="#0a0a0a"
        />
        <rect
          width="100%"
          height="100%"
          fill="url(#dotGrid)"
        />

        {/* Main content group with transform */}
        <g transform={`translate(${transform.x}, ${transform.y}) scale(${transform.scale})`}>
          {/* Drawings (render first, at the back) */}
          {drawings.map(drawing => (
            <MindMapDrawing key={drawing.id} drawing={drawing} />
          ))}
          {currentDrawing && (
            <MindMapDrawing drawing={currentDrawing} isActive />
          )}

          {/* Edges (render below nodes) */}
          {visibleEdges.map(edge => (
            <MindMapEdge
              key={edge.id}
              sourceNode={data.nodes[edge.sourceId]}
              targetNode={data.nodes[edge.targetId]}
              isCustom={edge.type === 'custom'}
            />
          ))}

          {/* Connector in progress */}
          {connectorStart && data.nodes[connectorStart] && (
            <line
              x1={data.nodes[connectorStart].x + 90}
              y1={data.nodes[connectorStart].y + 40}
              x2={data.nodes[connectorStart].x + 90}
              y2={data.nodes[connectorStart].y + 40}
              stroke="#a855f7"
              strokeWidth="2"
              strokeDasharray="5,5"
              className="pointer-events-none"
            />
          )}

          {/* Nodes */}
          {visibleNodes.map(node => (
            <MindMapNode
              key={node.id}
              node={node}
              isSelected={selectedNodeId === node.id}
              isEditing={editingNodeId === node.id}
              isConnectorSource={connectorStart === node.id}
              hasChildren={getChildren(node.id).length > 0}
              onSelect={(id) => {
                if (!handleNodeClickForConnector(id)) {
                  setSelectedNodeId(id);
                }
              }}
              onStartEdit={setEditingNodeId}
              onEndEdit={() => setEditingNodeId(null)}
              onUpdate={updateNode}
              onAddChild={addChildNode}
              onDelete={deleteNode}
              onToggleCollapse={toggleCollapse}
              onDragStart={handleNodeDragStart}
            />
          ))}
        </g>
      </svg>

      {/* Toolbar */}
      <MindMapToolbar
        scale={transform.scale}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onResetView={resetView}
        onAddNode={addRootNode}
        snapToGrid={snapToGrid}
        onToggleSnap={() => {
          const newValue = !snapToGrid;
          setSnapToGrid(newValue);
          setSnapToGridHook(newValue);
        }}
        toolMode={toolMode}
        onSetToolMode={setToolMode}
        drawColor={drawColor}
        onSetDrawColor={setDrawColor}
      />

      {/* Minimap */}
      <MindMapMinimap
        nodes={visibleNodes}
        edges={edges}
        transform={transform}
        canvasWidth={canvasSize.width}
        canvasHeight={canvasSize.height}
        onNavigate={setTransform}
      />

      {/* Context Menu */}
      <MindMapContextMenu
        isOpen={contextMenu.isOpen}
        position={{ x: contextMenu.x, y: contextMenu.y }}
        selectedNode={selectedNode}
        onClose={() => setContextMenu({ isOpen: false, x: 0, y: 0 })}
        onAddChild={() => selectedNodeId && addChildNode(selectedNodeId)}
        onEdit={() => selectedNodeId && setEditingNodeId(selectedNodeId)}
        onDelete={() => selectedNodeId && deleteNode(selectedNodeId)}
        onDuplicate={() => {
          // TODO: Implement duplicate
        }}
        onCollapse={() => selectedNodeId && toggleCollapse(selectedNodeId)}
      />

      {/* Save Status Indicator & Toggle */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-[30px] flex items-center gap-3 px-3 py-1.5 bg-[#0f1216]/90 border border-white/10 rounded-lg text-xs z-10">
        {/* Status indicator */}
        {isLoading ? (
          <>
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-zinc-400">Loading...</span>
          </>
        ) : saveStatus === 'saving' ? (
          <>
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
            <span className="text-zinc-400">Saving...</span>
          </>
        ) : saveStatus === 'saved' ? (
          <>
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-green-400">Saved</span>
          </>
        ) : saveStatus === 'error' ? (
          <>
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-red-400">Save failed</span>
          </>
        ) : !autoSaveEnabled && hasUnsavedChanges ? (
          <>
            <div className="w-2 h-2 rounded-full bg-orange-500" />
            <span className="text-orange-400">Unsaved changes</span>
          </>
        ) : (
          <>
            <div className="w-2 h-2 rounded-full bg-zinc-500" />
            <span className="text-zinc-500">{autoSaveEnabled ? 'Auto-save' : 'Local only'}</span>
          </>
        )}

        {/* Divider */}
        <div className="w-px h-4 bg-white/10" />

        {/* Toggle button */}
        <button
          onClick={toggleAutoSave}
          disabled={isLoading || saveStatus === 'saving'}
          className={`
            relative w-8 h-4 rounded-full transition-colors duration-200
            ${autoSaveEnabled ? 'bg-green-600' : 'bg-zinc-600'}
            ${(isLoading || saveStatus === 'saving') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}
          `}
          title={autoSaveEnabled ? 'Click to disable auto-save' : 'Click to enable auto-save & sync'}
        >
          <div
            className={`
              absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-200
              ${autoSaveEnabled ? 'translate-x-4' : 'translate-x-0.5'}
            `}
          />
        </button>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-[#0a0a0a]/80 flex items-center justify-center z-50">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-zinc-400 text-sm">Loading mind map...</span>
          </div>
        </div>
      )}

    </div>
  );
};

export default MindMapCanvas;
